import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { KPICard, TimeSeriesChart, FilterBar, createFilterState, filtersToParams } from '../../components/intelligence';
import { SkeletonKPI, SkeletonChart } from '../../components/intelligence/SkeletonLoader';

// State
const loading = signal(true);
const error = signal('');
const filters = createFilterState();

interface AggData {
  dau: number;
  wau: number;
  mau: number;
  sessions: number;
  retention_d1: number;
  retention_d7: number;
  retention_d30: number;
  conversion_rate: number;
  error_rate: number;
  latency_p50: number;
  latency_p95: number;
  latency_p99: number;
  llm_requests: number;
  llm_tokens: number;
  daily_events: Array<{ date: string; count: number }>;
  funnel?: Array<{ step: string; count: number }>;
  top_tools?: Array<{ tool: string; count: number }>;
}

const data = signal<AggData | null>(null);

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    const res = await api.get<{ aggregates: AggData }>(`/intelligence/aggregates?${params.toString()}`);
    data.value = res.aggregates || (res as any);
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar dados';
    // Set mock data for display
    data.value = {
      dau: 0, wau: 0, mau: 0, sessions: 0,
      retention_d1: 0, retention_d7: 0, retention_d30: 0,
      conversion_rate: 0, error_rate: 0,
      latency_p50: 0, latency_p95: 0, latency_p99: 0,
      llm_requests: 0, llm_tokens: 0,
      daily_events: [], funnel: [], top_tools: [],
    };
  } finally {
    loading.value = false;
  }
}

export function OverviewPanel() {
  useEffect(() => { loadData(); }, []);

  const d = data.value;

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Overview</h2>
      <FilterBar filters={filters} onApply={loadData} />

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">
          {error.value}
        </div>
      )}

      {/* KPIs */}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
        {loading.value ? (
          Array.from({ length: 12 }).map((_, i) => <SkeletonKPI key={i} />)
        ) : d ? (
          <>
 <KPICard icon="" label="DAU" value={d.dau} />
 <KPICard icon="" label="WAU" value={d.wau} />
 <KPICard icon="" label="MAU" value={d.mau} />
 <KPICard icon="" label="Sessões" value={d.sessions} />
 <KPICard icon="" label="Retenção D1" value={`${d.retention_d1}%`} />
 <KPICard icon="" label="Retenção D7" value={`${d.retention_d7}%`} />
 <KPICard icon="" label="Retenção D30" value={`${d.retention_d30}%`} />
 <KPICard icon="" label="Conversão" value={`${d.conversion_rate}%`} />
 <KPICard icon="" label="Taxa Erro" value={`${d.error_rate}%`} />
 <KPICard icon="" label="Latência p50" value={`${d.latency_p50}ms`} />
 <KPICard icon="" label="Latência p95" value={`${d.latency_p95}ms`} />
 <KPICard icon="" label="Latência p99" value={`${d.latency_p99}ms`} />
          </>
        ) : null}
      </div>

      {/* Charts */}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {loading.value ? (
          <>
            <SkeletonChart />
            <SkeletonChart />
          </>
        ) : (
          <>
            <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&>div]:text-gray-200">
              <TimeSeriesChart
                title="Eventos Diários"
                data={(d?.daily_events || []).map((e) => ({ label: e.date, value: e.count }))}
                color="#6366f1"
              />
            </div>

            {/* Funnel */}
            <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
              <div class="text-sm font-medium text-gray-300 mb-4">Funil de Conversão</div>
              {(d?.funnel || []).length === 0 ? (
                <div class="text-gray-500 text-sm text-center py-8">Sem dados de funil</div>
              ) : (
                <div class="space-y-3">
                  {(d?.funnel || []).map((step, i, arr) => {
                    const maxCount = arr[0]?.count || 1;
                    const pct = (step.count / maxCount) * 100;
                    return (
                      <div key={i}>
                        <div class="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{step.step}</span>
                          <span>{step.count.toLocaleString()} ({pct.toFixed(1)}%)</span>
                        </div>
                        <div class="h-6 bg-gray-700 rounded-lg overflow-hidden">
                          <div
                            class="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 rounded-lg transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tool Heatmap */}
      <div class="bg-gray-800 rounded-xl border border-gray-700 p-5">
        <div class="text-sm font-medium text-gray-300 mb-4">Uso por Ferramenta (Heatmap)</div>
        {loading.value ? (
          <div class="h-32 bg-gray-700/30 rounded animate-pulse" />
        ) : (d?.top_tools || []).length === 0 ? (
          <div class="text-gray-500 text-sm text-center py-8">Sem dados de uso</div>
        ) : (
          <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {(d?.top_tools || []).map((t, i) => {
              const max = (d?.top_tools || [])[0]?.count || 1;
              const intensity = Math.round((t.count / max) * 100);
              return (
                <div
                  key={i}
                  class="rounded-lg p-3 text-center"
                  style={{ backgroundColor: `rgba(99, 102, 241, ${intensity / 100 * 0.6 + 0.1})` }}
                >
                  <div class="text-xs font-medium text-white">{t.tool}</div>
                  <div class="text-lg font-bold text-white">{t.count.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
